import { createInjunctionDecision } from "@/api/cle-api";
import {
  createPersonPaabud,
  deletePersonPaabud,
  getPersonPaabud,
  getPersonPaabudList,
  patchPersonPaabud,
  type CreatePersonPaabudBody,
  type PersonPaabudDTO,
  type UpdatePersonPaabudFields,
} from "@/api/person/paabud";
import { getPatchOperations } from "@/util/form";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

export interface UsePersonPaabudListParams {
  personId?: string;
}

export interface UsePersonPaabudParams {
  personId?: string;
  paabudId?: string;
}

export const personPaabudListQueryKey = (personId: string) =>
  ["personPaabud", personId] as const;

export const personPaabudQueryKey = (personId: string, paabudId: string) =>
  ["personPaabud", personId, paabudId] as const;

export const usePersonPaabudList = ({
  personId,
}: UsePersonPaabudListParams): UseQueryResult<PersonPaabudDTO[], Error> =>
  useQuery({
    queryKey: personPaabudListQueryKey(personId ?? ""),
    queryFn: () => getPersonPaabudList(personId ?? ""),
    enabled: Boolean(personId),
  });

export const usePersonPaabud = ({
  personId,
  paabudId,
}: UsePersonPaabudParams): UseQueryResult<PersonPaabudDTO, Error> =>
  useQuery({
    queryKey: personPaabudQueryKey(personId ?? "", paabudId ?? ""),
    queryFn: () => getPersonPaabud(personId ?? "", paabudId ?? ""),
    enabled: Boolean(personId) && Boolean(paabudId),
  });

export interface CreatePersonPaabudParams {
  personId: string;
  body: Omit<
    CreatePersonPaabudBody,
    "decisionDocumentFileName" | "decisionDocumentContentType"
  >;
  file: File;
}

export const useCreatePersonPaabud = (): UseMutationResult<
  PersonPaabudDTO,
  Error,
  CreatePersonPaabudParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ personId, body, file }) => {
      const paabud = await createPersonPaabud(personId, {
        ...body,
        decisionDocumentFileName: file.name,
        decisionDocumentContentType: file.type || "application/pdf",
      });

      await createInjunctionDecision({
        userId: personId,
        externalInjunctionId: paabud.id,
        file,
      });

      return paabud;
    },
    onSuccess: (_data, { personId }) => {
      queryClient.invalidateQueries({
        queryKey: personPaabudListQueryKey(personId),
      });
      queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", personId, "injunction-decisions"],
      });
    },
  });
};

export interface UpdatePersonPaabudParams {
  personId: string;
  paabudId: string;
  changes: UpdatePersonPaabudFields;
}

export const useUpdatePersonPaabud = (): UseMutationResult<
  PersonPaabudDTO,
  Error,
  UpdatePersonPaabudParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, paabudId, changes }) =>
      patchPersonPaabud(personId, paabudId, getPatchOperations(changes)),
    onSuccess: (data, { personId, paabudId }) => {
      queryClient.invalidateQueries({
        queryKey: personPaabudListQueryKey(personId),
      });
      queryClient.invalidateQueries({
        queryKey: personPaabudQueryKey(personId, paabudId),
      });
      queryClient.invalidateQueries({
        queryKey: personPaabudQueryKey(personId, data.id),
      });
    },
  });
};

export interface DeletePersonPaabudParams {
  personId: string;
  paabudId: string;
}

export const useDeletePersonPaabud = (): UseMutationResult<
  PersonPaabudDTO,
  Error,
  DeletePersonPaabudParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, paabudId }) =>
      deletePersonPaabud(personId, paabudId),
    onSuccess: (_data, { personId, paabudId }) => {
      queryClient.invalidateQueries({
        queryKey: personPaabudListQueryKey(personId),
      });
      queryClient.invalidateQueries({
        queryKey: personPaabudQueryKey(personId, paabudId),
      });
    },
  });
};
